/**
 * Add RFP Details worksheet to workbook
 *
 * Fetches RFP details, client details, and brokerage info,
 * then creates a formatted worksheet with field names and values.
 */

import ExcelJS from "exceljs";
import startCase from "lodash/startCase";

import type { AxiosInstance } from "axios";
import type {
  RfpDetailsResponse,
  RfpClientDetailsResponse,
  RfpBrokerageResponse,
} from "./types";

const fetchRfpDetails = async (
  rfpId: string,
  axiosInstance: AxiosInstance,
): Promise<RfpDetailsResponse> => {
  const response = await axiosInstance.get<RfpDetailsResponse>(
    `brokerportal/v1/rfp/${rfpId}/details`,
  );
  return response.data;
};

const fetchRfpClientDetails = async (
  rfpId: string,
  axiosInstance: AxiosInstance,
): Promise<RfpClientDetailsResponse> => {
  const response = await axiosInstance.get<RfpClientDetailsResponse>(
    `brokerportal/v1/rfp/${rfpId}/client`,
  );
  return response.data;
};

const fetchRfpBrokerage = async (
  rfpId: string,
  axiosInstance: AxiosInstance,
): Promise<RfpBrokerageResponse> => {
  const response = await axiosInstance.get<RfpBrokerageResponse>(
    `brokerportal/v1/rfp/${rfpId}/brokerage`,
  );
  return response.data;
};

const formatAddress = (
  line1?: string | null,
  line2?: string | null,
  city?: string | null,
  state?: string | null,
  zip?: string | null,
): string => {
  const parts = [line1, line2, city, state, zip].filter(Boolean);
  return parts.join(", ");
};

const formatDate = (date?: string | null): string => {
  if (!date) return "";
  const [datePart] = date.split("T");
  const [year, month, day] = datePart.split("-").map(Number);
  return new Date(year, month - 1, day).toLocaleDateString();
};

const formatName = (
  firstName?: string | null,
  lastName?: string | null,
): string => `${firstName || ""} ${lastName || ""}`.trim();

type AddFieldFn = (label: string, value: string | null | undefined) => void;

const TECH_CREDIT_TYPE_LABELS: Record<string, string> = {
  percentage: "Percentage",
  dollarAmount: "Dollar Amount",
  pepm: "PEPM",
  carrierStandard: "Carrier Standard",
};

const formatTechCreditAmount = (type: string, amount: number): string => {
  if (type === "percentage") return `${amount}%`;
  if (type === "dollarAmount" || type === "pepm") return `$${amount}`;
  return amount.toString();
};

const addClientFields = (
  addField: AddFieldFn,
  client: RfpClientDetailsResponse,
): void => {
  addField("Group Name", client?.name);
  addField("Situs State", client?.situsStateName || client?.situsState);
  addField(
    "Client Address",
    formatAddress(
      client?.address?.addressLine1,
      client?.address?.addressLine2,
      client?.address?.city,
      client?.address?.state,
      client?.address?.zipCode,
    ),
  );
  addField("Eligible Employees", client?.numberOfEmployees?.toString());
  addField(
    "International Employees",
    client?.hasInternationalEmployees ? "Yes" : "No",
  );
};

const addClientAdditionalInfoFields = (
  addField: AddFieldFn,
  client: RfpClientDetailsResponse,
): void => {
  addField(
    "BenAdmin System",
    client?.rfpAdditionalInfo?.requestedSystem
      ? startCase(client.rfpAdditionalInfo.requestedSystem)
      : null,
  );
  addField(
    "Technology Credits",
    client?.rfpAdditionalInfo?.technologyCreditNeeded ? "Yes" : "No",
  );

  if (client?.rfpAdditionalInfo?.technologyCreditNeeded) {
    const creditType = client?.rfpAdditionalInfo?.techCreditType;
    addField(
      "Technology Credit Type",
      creditType ? (TECH_CREDIT_TYPE_LABELS[creditType] ?? creditType) : null,
    );
    if (creditType && creditType !== "carrierStandard") {
      addField(
        "Technology Credit Amount",
        client?.rfpAdditionalInfo?.techCreditAmount != null
          ? formatTechCreditAmount(
              creditType,
              client.rfpAdditionalInfo.techCreditAmount,
            )
          : null,
      );
    }
  }
};

const addBrokerageFields = (
  addField: AddFieldFn,
  brokerage: RfpBrokerageResponse,
): void => {
  addField("Broker Name", brokerage?.name);
  addField(
    "Broker Address",
    formatAddress(
      brokerage?.address?.address,
      brokerage?.address?.addressLine2,
      brokerage?.address?.city,
      brokerage?.address?.state,
      brokerage?.address?.zipcode,
    ),
  );
};

const addProducerFields = (
  addField: AddFieldFn,
  brokerage: RfpBrokerageResponse,
): void => {
  addField(
    "Producer Name",
    brokerage?.clientInfo?.producer
      ? formatName(
          brokerage.clientInfo.producer.firstName,
          brokerage.clientInfo.producer.lastName,
        )
      : "",
  );
  addField("Producer Email", brokerage?.clientInfo?.producer?.email);
  addField(
    "Brokers Account Manager",
    formatName(brokerage?.submitter.firstName, brokerage?.submitter.lastName),
  );
};

// The analyst slot holds either a Centro analyst or a broker_analyst —
// mutually exclusive populations stored in different fields (ADR 10.19
// amendment). The Centro analyst wins if legacy data ever has both,
// matching the carrier payload's precedence in BuildRfpDetailsResponse.
// A broker_analyst removed from Auth0 comes back with only an email, so
// fall back to it for the name rather than rendering a blank.
const toAssignedAnalyst = (
  details: RfpDetailsResponse,
): { name: string | undefined; email: string | undefined } => {
  if (details?.primaryAnalyst) {
    return {
      name: details.primaryAnalyst.name,
      email: details.primaryAnalyst.email,
    };
  }
  if (details?.assignedBrokerAnalyst) {
    return {
      name:
        details.assignedBrokerAnalyst.name ||
        details.assignedBrokerAnalyst.email,
      email: details.assignedBrokerAnalyst.email,
    };
  }
  return { name: undefined, email: undefined };
};

const addRfpDetailsFields = (
  addField: AddFieldFn,
  details: RfpDetailsResponse,
): void => {
  const analyst = toAssignedAnalyst(details);

  addField(
    "Effective Date",
    formatDate(details?.revisedEffectiveDate || details?.originalEffectiveDate),
  );
  addField("Due Date", formatDate(details?.carrierDueDate));
  addField("General Agent", "Centro Benefits Research LLC");
  addField("Primary RFP Analyst Name", analyst.name);
  addField("Primary RFP Analyst Email", analyst.email);
};

const buildWorksheetContent = (
  worksheet: ExcelJS.Worksheet,
  details: RfpDetailsResponse,
  client: RfpClientDetailsResponse,
  brokerage: RfpBrokerageResponse,
): void => {
  const addField: AddFieldFn = (label, value) => {
    const row = worksheet.addRow([label, value || ""]);
    row.getCell(1).font = { bold: true };
    row.getCell(1).alignment = { horizontal: "left", vertical: "middle" };
    row.getCell(2).alignment = { horizontal: "left", vertical: "middle" };
  };

  addClientFields(addField, client);
  addBrokerageFields(addField, brokerage);
  addProducerFields(addField, brokerage);
  addRfpDetailsFields(addField, details);
  addClientAdditionalInfoFields(addField, client);

  worksheet.getColumn(1).width = 30;
  worksheet.getColumn(2).width = 50;
};

export const addRfpDetailsWorksheet = async (
  workbook: ExcelJS.Workbook,
  rfpId: string,
  axiosInstance: AxiosInstance,
): Promise<void> => {
  const [details, client, brokerage] = await Promise.all([
    fetchRfpDetails(rfpId, axiosInstance),
    fetchRfpClientDetails(rfpId, axiosInstance),
    fetchRfpBrokerage(rfpId, axiosInstance),
  ]);

  const worksheet = workbook.addWorksheet("RFP Details");

  const headerRow = worksheet.addRow(["RFP Details"]);
  headerRow.font = { bold: true, size: 14 };
  headerRow.alignment = { horizontal: "left", vertical: "middle" };

  buildWorksheetContent(worksheet, details, client, brokerage);
};
